import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GoogleMap, Polygon } from '@react-google-maps/api';
import { faExpand } from '@fortawesome/free-solid-svg-icons';

//STYLES
import { ContainerPontosColeta, ModalMalhaGeral, FooterModal, ColunaMapa, WrapperMapa, AcoesInfo } from './styled';

//APP
import { LabAnaliseItens } from '../../../../../../../../../../../../../../services/LabAnalise';
import { calculoAreaCoord } from '../../../../../../../../../../../../../../services/Afins';
import { PropsAnalisesCadastradas } from '../../../../../../types';
import { errorAlert, warningAlert } from '../../../../../../../../../../../../../../utils/alert';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../../../../../Components/ButtonCustom';
import { Render } from '../../../../../../../../../../../../../../Components/CustomUi/render';
import { CardFullscreen } from './components/cardFullScreen';
import { ControleLateralModal } from './components/controleLateralModal';
import { obterAnalisesEmUso } from '../../../../meta/analisesEmUso';
import { localizarAnaliseMicro } from '../../../../meta/autoAnalise';
import { InfosMalha } from './components/infosMalha';
import { AlteracoesMalha } from './components/alteracoesMalha';
import { MarkersMalha } from './components/markersMalha';
import { OverlayAnalise } from './components/overlayAnalise';
import { AcaoSalvar } from './components/acaoSalvar';
import { chavePonto, chavesPontosJaUsados } from './components/selecaoEmMassa/meta';
import { orderBy } from 'lodash';

// --- NOVA TIPAGEM PLANA (Sem geometry/properties) ---
export interface GeoPointFeature {
    INDICE: number
    NOME: number | string
    LATITUDE: number
    LONGITUDE: number
    COR?: string | null
    id?: string | number
    [key: string]: any
}

// As propriedades específicas que ficam dentro de "properties"
export interface MalhaProperties {
    INDICE: string
    NOME: string
    COMENTARIO: string | null
    ICONE: string | null
    ALTITUDE: string | null
    DATA: string | null
    ANGULO: string | null
    DISPLAY: string | null
    ZONA: string | null
    LATITUDE: string
    LONGITUDE: string
}

// A estrutura de cada item dentro do array "features"
export interface MalhaFeature {
    type: 'Feature'
    geometry: {
        type: 'Point'
        coordinates: [string, string] // [longitude, latitude] como string conforme seu JSON
    }
    properties: MalhaProperties
    color: string
}

// O objeto principal (FeatureCollection)
export interface MalhaGeoJson {
    type: 'FeatureCollection'
    features: MalhaFeature[]
    fileName: string
}

interface MapModalProps {
    isOpen: boolean
    onClose: () => void
    talhaoGeoJson: any
    initialPoints: GeoPointFeature[] | any
    tipo_analise_selecionada: string
    setTipo_analise_selecionada: React.Dispatch<React.SetStateAction<string>>
    tipo_analise_selecionada_id: number
    setTipo_analise_selecionada_id: React.Dispatch<React.SetStateAction<number>>
    pontos_selecionados: GeoPointFeature[]
    setPontos_selecionados: React.Dispatch<React.SetStateAction<GeoPointFeature[]>>
    selecionarTodos: boolean
    setSelecionarTodos: React.Dispatch<React.SetStateAction<boolean>>
    pontos_selecionados_adicionados: PropsAnalisesCadastradas[]
    setPontos_selecionados_adicionados: React.Dispatch<
        React.SetStateAction<PropsAnalisesCadastradas[]>
    >
    malhaShp: MalhaGeoJson[]
    setMalhaShp: React.Dispatch<React.SetStateAction<MalhaGeoJson[]>>
    grupoAnalises: LabAnaliseItens[]
    id: string | number
    cor: string
    setCor: React.Dispatch<React.SetStateAction<string>>
    grupoAnalisesTratado: any[]
    salvarPontosSelecionados: Function
    /** Análises já salvas no formulário, usadas só para leitura na seleção em massa. */
    analises_salvas?: any[]
    /** `true` durante a 2ª passada do fluxo de micronutrientes. */
    em_ciclo_micro?: boolean
    /** Inicia o ciclo (após adicionar a análise da 1ª passada). */
    iniciarCicloMicro?: () => void
    /** Conclui a 2ª passada com os pontos que levam micro. */
    concluirSelecaoMicro?: (pontos: any[]) => void
}

// --- Estilos ---
// Agora height 100% pois será controlado pelo pai
const mapContainerStyle = {
    width: '100%',
    height: '100%',
    // borderRadius: '8px', // Controlado pelo wrapper se necessário
    overflow: 'hidden',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
}

const talhaoOptions = {
    fillColor: '#14b9c5',
    fillOpacity: 0.25,
    strokeColor: '#00e1ff',
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
    zIndex: 1,
}

export const ModalNovaAnalise: React.FC<MapModalProps> = ({ isOpen, onClose, talhaoGeoJson, initialPoints,
    tipo_analise_selecionada, setTipo_analise_selecionada, tipo_analise_selecionada_id, setTipo_analise_selecionada_id,
    pontos_selecionados, setPontos_selecionados, pontos_selecionados_adicionados, setPontos_selecionados_adicionados, selecionarTodos, setSelecionarTodos,
    malhaShp, setMalhaShp, grupoAnalises, salvarPontosSelecionados, id, cor, setCor,
    grupoAnalisesTratado, analises_salvas = [],
    em_ciclo_micro = false, iniciarCicloMicro, concluirSelecaoMicro
}) => {

    const [points, setPoints] = useState<GeoPointFeature[]>([])

    const [inserirPontos, setInserirPontos] = useState<boolean>(false)
    const [moverPontos, setMoverPontos] = useState<boolean>(false)
    const [fake_loading, setFake_loading] = useState<boolean>(false)
    const [fullscreen, setFullscreen] = useState<boolean>(false)
    const mapRef = useRef<google.maps.Map | null>(null)
    const polygonInstanceRef = useRef<google.maps.Polygon | null>(null)
    const fitBoundsDone = useRef(false)
    const wrapperRef = useRef<HTMLDivElement | null>(null)

    // 1. Carrega os pontos iniciais
    useEffect(() => {
        if (initialPoints && Array.isArray(initialPoints) && !id) {
            setPoints(initialPoints)
        }
    }, [initialPoints])

    /**
     * Ao fechar o modal, garante que o navegador também saia da tela cheia.
     *
     * `destroyOnClose` remove o wrapper do DOM; se o fullscreen ainda estivesse ativo
     * sobre ele, o navegador ficaria em tela cheia sobre um nó inexistente. Só zerar o
     * estado local do React não basta — é preciso chamar o `exitFullscreen`.
     */
    useEffect(() => {
        if (!isOpen) {
            fitBoundsDone.current = false
            setFullscreen(false)

            if (document.fullscreenElement) {
                const sairTelaCheia =
                    document.exitFullscreen?.bind(document) ??
                    (document as any).webkitExitFullscreen?.bind(document)
                Promise.resolve(sairTelaCheia?.()).catch(() => { })
            }
        }
    }, [isOpen, JSON.stringify(talhaoGeoJson)])

    // --- FULLSCREEN CHANGE LISTENER ---
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFull = !!document.fullscreenElement
            setFullscreen(isFull)
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange) // Safari/Chrome antigo
        document.addEventListener('mozfullscreenchange', handleFullscreenChange) // Firefox antigo
        document.addEventListener('msfullscreenchange', handleFullscreenChange) // IE/Edge antigo

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
            document.removeEventListener('msfullscreenchange', handleFullscreenChange)
        }
    }, [])

    /**
     * Alterna a tela cheia no wrapper do mapa.
     *
     * O estado `fullscreen` não é alterado aqui: quem manda é o listener de
     * `fullscreenchange`, para que o ESC do navegador e este botão convirjam.
     * `requestFullscreen` pode ser negado (iframe sem permissão, política do
     * navegador) e rejeita a Promise — sem o catch o clique não faria nada e o
     * usuário ficaria sem resposta.
     */
    const toggleFullscreen = useCallback(() => {
        if (!wrapperRef.current) return

        if (!document.fullscreenElement) {
            const pedirTelaCheia =
                wrapperRef.current.requestFullscreen?.bind(wrapperRef.current) ??
                (wrapperRef.current as any).webkitRequestFullscreen?.bind(wrapperRef.current)

            if (!pedirTelaCheia) {
                return errorAlert('Seu navegador não suporta o modo tela cheia.')
            }

            Promise.resolve(pedirTelaCheia()).catch(() => {
                errorAlert('Não foi possível abrir a tela cheia. Verifique as permissões do navegador.')
            })
        } else {
            const sairTelaCheia =
                document.exitFullscreen?.bind(document) ??
                (document as any).webkitExitFullscreen?.bind(document)

            Promise.resolve(sairTelaCheia?.()).catch(() => {
                /* Sair da tela cheia falhando não bloqueia nada: o listener mantém o estado correto. */
            })
        }
    }, [])

    const defaultCenter = useMemo(() => ({ lat: -15, lng: -47 }), [])

    const talhaoPath = useMemo(() => {
        if (!Array.isArray(talhaoGeoJson)) return []
        // Normaliza para {lat, lng} numérico (aceita {lat,lng} ou {latitude,longitude})
        // e descarta coordenadas inválidas — evita crash do Google Maps.
        return talhaoGeoJson
            .map((c: any) => ({
                lat: Number(c?.lat ?? c?.latitude),
                lng: Number(c?.lng ?? c?.longitude),
            }))
            .filter((c: any) => !isNaN(c.lat) && !isNaN(c.lng))
    }, [JSON.stringify(talhaoGeoJson)])

    /** Área do talhão em hectares. Memoizada: era recalculada 5x por render. */
    const talhao_area = useMemo(() => calculoAreaCoord([talhaoPath]), [talhaoPath])

    /**
     * Pontos que já pertencem a alguma análise — os grupos adicionados nesta sessão mais as
     * análises já salvas no formulário.
     *
     * É o que dá ao marker seu terceiro estado. Memoizado porque o `MarkersMalha` consulta
     * este Set uma vez por ponto, e a malha chega a centenas deles.
     */
    const pontos_ja_usados = useMemo(
        () => chavesPontosJaUsados(pontos_selecionados_adicionados, analises_salvas),
        [pontos_selecionados_adicionados, analises_salvas]
    )

    /**
     * Análises já usadas na OS, desabilitadas no select. Ao editar um grupo
     * (`id`), a análise dele fica de fora — senão o usuário não conseguiria
     * salvar a própria edição.
     */
    const analises_em_uso = useMemo(
        () => obterAnalisesEmUso(analises_salvas, pontos_selecionados_adicionados, id),
        [analises_salvas, pontos_selecionados_adicionados, id]
    )

    /**
     * A análise de micronutrientes já existe nesta OS? Se sim, o Popconfirm
     * "Deseja Gerar Micronutrientes?" não deve mais aparecer — o fluxo já foi
     * cumprido, e repetir levaria a análises duplicadas.
     * Equivale ao `Col hidden={micro}` da produção.
     */
    const micro_ja_adicionado = useMemo(() => {
        const cod_micro = localizarAnaliseMicro(grupoAnalises ?? [])?.cod_analise
        if (!cod_micro) return false

        return analises_em_uso.includes(cod_micro)
    }, [grupoAnalises, analises_em_uso])

    /**
     * Quantos pontos da malha ainda não entraram em nenhuma análise.
     *
     * Os pontos marcados agora entram na conta como já resolvidos, mesmo antes de o grupo
     * ser adicionado — caso contrário o contador só zeraria depois do "Adicionar" e o
     * usuário veria pendência no que acabou de marcar.
     */
    const pontos_nunca_usados = useMemo(
        () =>
            points.filter((ponto) => !ponto.COR && !pontos_ja_usados.has(chavePonto(ponto))).length,
        [points, pontos_ja_usados]
    )

    const fitBoundsMap = (mapInstance: google.maps.Map) => {
        if (!mapInstance || fitBoundsDone.current) return

        if (talhaoPath.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            talhaoPath.forEach((coord: any) => bounds.extend(coord))
            mapInstance.fitBounds(bounds)
            fitBoundsDone.current = true
        } else if (points.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            let hasValidPoint = false
            points.forEach((pt) => {
                const latVal = Number(pt.LATITUDE)
                const lngVal = Number(pt.LONGITUDE)

                if (!isNaN(latVal) && !isNaN(lngVal) && latVal !== 0 && lngVal !== 0) {
                    bounds.extend({ lat: latVal, lng: lngVal })
                    hasValidPoint = true
                }
            })

            if (hasValidPoint && !bounds.isEmpty()) {
                mapInstance.fitBounds(bounds)
                fitBoundsDone.current = true
            }
        }
    }

    useEffect(() => {
        if (isOpen && mapRef.current && !fitBoundsDone.current) {
            // Pequeno delay para garantir que o mapa esteja renderizado
            const timer = setTimeout(() => {
                fitBoundsMap(mapRef.current!)
            }, 300)
            return () => clearTimeout(timer)
        }
    }, [isOpen, talhaoPath, points])

    /**
     * Reenquadra o talhão ao entrar/sair da tela cheia.
     *
     * O container muda de tamanho drasticamente, mas o Google Maps preserva o centro e o
     * zoom — o talhão acabava descentralizado justamente na tela onde se quer precisão.
     * O `fitBounds` só vale depois do resize, por isso o delay; e é preciso liberar o
     * `fitBoundsDone` porque ele já está marcado do enquadramento inicial.
     */
    useEffect(() => {
        if (!isOpen || !mapRef.current) return

        const timer = setTimeout(() => {
            if (!mapRef.current) return
            google.maps.event.trigger(mapRef.current, 'resize')
            fitBoundsDone.current = false
            fitBoundsMap(mapRef.current)
        }, 150)

        return () => clearTimeout(timer)
    }, [fullscreen])

    // Handlers
    const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
        polygonInstanceRef.current = polygon
    }, [])

    const adicionarNovoPontoMalha = useCallback(
        (event: google.maps.MapMouseEvent) => {
            // 1. Verificações de segurança
            if (!inserirPontos || !event.latLng || !polygonInstanceRef.current) return

            const isInside = google.maps.geometry.poly.containsLocation(
                event.latLng,
                polygonInstanceRef.current
            )

            if (!isInside) {
                warningAlert('O ponto deve ser inserido dentro do talhão!', undefined, undefined, 'point-inside-talhao')
                return
            }

            const lat = event.latLng.lat()
            const lng = event.latLng.lng()

            let novoIndiceNum = 1 // Começa com 1 se a lista estiver vazia
            //@ts-ignore
            const novoNome = (malhaShp.features.length + 1).toString()
            // @ts-ignore
            const features = malhaShp?.features

            if (features && features.length > 0) {
                // Pega o último objeto do array
                const ultimoObjeto = features[features.length - 1]

                const ultimoIndiceVal = parseInt(ultimoObjeto?.properties?.INDICE)

                // Se for um número válido, soma 1
                if (!isNaN(ultimoIndiceVal)) {
                    novoIndiceNum = ultimoIndiceVal + 1
                }
            }

            const novoIndiceStr = novoIndiceNum.toString()

            // @ts-ignore
            const newFeature = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lng.toString(), lat.toString()],
                },
                properties: {
                    INDICE: novoIndiceStr, // Agora sequencial (ex: "392")
                    NOME: novoNome, // Geralmente o Nome segue o Indice
                    COMENTARIO: null,
                    ICONE: null,
                    ALTITUDE: null,
                    DATA: null,
                    ANGULO: null,
                    DISPLAY: null,
                    ZONA: null,
                    LATITUDE: lat.toString(),
                    LONGITUDE: lng.toString(),
                },
                color: '',
            }

            setMalhaShp((prev: any) => ({
                ...prev,
                features: [...prev.features, newFeature],
            }))

            const newPoint: GeoPointFeature = {
                id: novoIndiceNum,
                NOME: novoNome,
                INDICE: novoIndiceNum,
                LATITUDE: event.latLng.lat(),
                LONGITUDE: event.latLng.lng(),
                COR: null
            }
            setPoints((prev) => [...prev, newPoint])
        },
        // IMPORTANTE: Adicione malhaShp nas dependências para ler o estado atualizado
        // @ts-ignore
        [inserirPontos, malhaShp]
    )

    const moverPontosMalha = (index: number, event: google.maps.MapMouseEvent) => {
        if (!event.latLng || !polygonInstanceRef.current) return

        const isInside = google.maps.geometry.poly.containsLocation(
            event.latLng,
            polygonInstanceRef.current
        )

        if (!isInside) {
            errorAlert('Não é permitido mover o ponto para fora do talhão.')
            setPoints((prev) => [...prev])
            return
        }

        const newLng = event.latLng.lng()
        const newLat = event.latLng.lat()

        setPoints((prev) => {
            const newPoints = [...prev]
            newPoints[index] = {
                ...newPoints[index],
                LATITUDE: newLat,
                LONGITUDE: newLng,
            }
            return newPoints
        })

        setPontos_selecionados((prev) => {
            const pointId = points[index].NOME
            const isSelected = prev.some((p) => p.NOME === pointId)

            if (isSelected) {
                return prev.map((p) => {
                    if (p.NOME === pointId) {
                        return {
                            ...p,
                            LATITUDE: newLat,
                            LONGITUDE: newLng,
                        }
                    }
                    return p
                })
            }
            return prev
        })
    }

    const selecionarPontosMalha = useCallback(
        (index: number) => {
            if (inserirPontos)
                return warningAlert('Desative o inserir pontos para selecionar o ponto desejado', undefined, undefined, 'disable-insert-points')

            const targetPoint = points[index]
            const pointId = targetPoint.NOME

            const isSelected = pontos_selecionados.some((p) => p.NOME === pointId)

            if (isSelected) {
                setPontos_selecionados((prev) => prev.filter((p) => p.NOME !== pointId))

                setPoints((prev) => {
                    const newPoints = [...prev]
                    newPoints[index] = {
                        ...newPoints[index],
                        COR: null,
                    }
                    return newPoints
                })
            } else {
                if (!tipo_analise_selecionada) {
                    return warningAlert('Selecione uma análise antes de marcar um ponto.', undefined, undefined, 'select-analysis-before-mark')
                }

                if (pontos_selecionados.length > 0) {
                    let corDaSelecaoExistente = pontos_selecionados[0].COR

                    if (cor !== corDaSelecaoExistente) {
                        return warningAlert(
                            'Você não pode misturar análises na mesma seleção. Salve ou limpe os pontos atuais antes de mudar de análise.',
                            undefined,
                            undefined,
                            'mix-analysis-selection'
                        )
                    }
                }

                const pointWithColor = {
                    ...targetPoint,
                    COR: cor,
                }

                setPontos_selecionados((prev) => [...prev, pointWithColor])

                setPoints((prev) => {
                    const newPoints = [...prev]
                    newPoints[index] = pointWithColor
                    return newPoints
                })
            }
        },
        [inserirPontos, cor, tipo_analise_selecionada, points, pontos_selecionados]
    )

    /**
     * 1ª passada do fluxo de micronutrientes: adiciona a análise atual e entra no
     * ciclo, limpando a seleção para o usuário escolher quais pontos levam micro.
     */
    function gerarMicronutrientes() {
        if (!adicionarPontosSelecionados(true)) return

        iniciarCicloMicro?.()
    }

    /** 2ª passada concluída: entrega os pontos com micro ao hook. */
    function concluirMicronutrientes() {
        if (pontos_selecionados.length === 0) {
            return warningAlert(
                'Selecione os pontos que também levam micronutrientes.',
                undefined,
                undefined,
                'select-points-micro'
            )
        }

        // Guarda os pontos ANTES de adicionar: `adicionarPontosSelecionados` zera
        // a seleção, e são eles a base das análises complementares.
        const pontosDoMicro = [...pontos_selecionados]

        // A própria análise de micronutrientes precisa ser adicionada como
        // qualquer outra — sem isso a seleção do usuário se perde quando ele não
        // marca nenhuma complementar.
        if (!adicionarPontosSelecionados(true)) return

        concluirSelecaoMicro?.(pontosDoMicro)
        limparAnaliseSelecionada()
    }

    /** @returns `true` quando a análise foi adicionada; `false` se alguma validação barrou. */
    function adicionarPontosSelecionados(manterCorAoAdicionar = false): boolean {
        if (pontos_selecionados.length === 0) {
            warningAlert('Selecione pontos no mapa antes de adicionar.', undefined, undefined, 'select-points-before-add')
            return false
        }

        const corDosPontos = pontos_selecionados[0].COR
        if (cor !== corDosPontos) {
            warningAlert(
                'A análise selecionada é diferente da análise dos pontos marcados. Selecione a análise correta ou limpe a seleção.',
                undefined,
                undefined,
                'analysis-mismatch'
            )
            return false
        }

        const corJaCadastrada = pontos_selecionados_adicionados.some((item) => item?.cor === cor)
        if (corJaCadastrada) {
            warningAlert(
                'Já existe um grupo de pontos adicionado com esta análise. Por favor, escolha outra análise.',
                undefined,
                undefined,
                'group-color-exists'
            )
            return false
        }

        const profundidade = grupoAnalises?.filter(
            (item) => item?.cod_analise === tipo_analise_selecionada_id
        )

        const filtrar = grupoAnalises
            ?.filter((item) => item?.cod_analise === tipo_analise_selecionada_id)
            .map((item) => item?.valor)

        const pontos_adicionar = {
            id: Date.now(),
            cod_analise: tipo_analise_selecionada_id,
            pontos_selecionados: pontos_selecionados,
            profundidade: profundidade[0]?.profundidade_nome,
            analise_nome: profundidade[0]?.analise_nome,
            valor_analise: (Number(filtrar[0]) * pontos_selecionados?.length).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
            }),
            valor: filtrar[0],
            cor: cor,
        }

        setPontos_selecionados_adicionados((prev) => [...prev, pontos_adicionar])

        setPoints((prev) =>
            prev.map((p) => {
                const estavaSelecionado = pontos_selecionados.some((sel) => sel.NOME === p.NOME)
                if (estavaSelecionado) {
                    return {
                        ...p,
                        // No fluxo de micronutrientes os pontos precisam MANTER a cor
                        // da análise: é por ela que o "restante" (00-20 Macro+S) é
                        // calculado depois. Fora do ciclo, voltam a ficar livres.
                        COR: manterCorAoAdicionar ? cor : null,
                    }
                }
                return p
            })
        )

        setPontos_selecionados([])
        setSelecionarTodos(false)

        // Limpa o select: mantê-lo preenchido induz o usuário a marcar pontos
        // achando que é outra análise. No fluxo de micronutrientes NÃO limpamos —
        // `iniciarCicloMicro` roda logo depois e define a análise de micro.
        if (!manterCorAoAdicionar) {
            limparAnaliseSelecionada()
        }

        return true
    }

    /** Zera o select de análise (label, id e cor). */
    function limparAnaliseSelecionada() {
        setTipo_analise_selecionada('')
        setTipo_analise_selecionada_id(null)
        setCor('')
    }

    const selecionarTodosPontos = useCallback(
        (checked: boolean) => {

            if (tipo_analise_selecionada_id) {
                setSelecionarTodos(checked)
                if (checked) {
                    const corParaAplicar = cor || '#ffffff'

                    setPoints((prevPoints) => {
                        return prevPoints.map((p) => ({
                            ...p,
                            COR: corParaAplicar,
                        }))
                    })

                    setPontos_selecionados(() => {
                        return points.map((p) => ({
                            ...p,
                            COR: corParaAplicar,
                        }))
                    })
                } else {
                    setPoints((prevPoints) => {
                        return prevPoints.map((p) => ({
                            ...p,
                            COR: null,
                        }))
                    })

                    setPontos_selecionados([])
                }
            } else {
                warningAlert('Selecione uma análise no menu lateral antes de selecionar todos os pontos.', undefined, undefined, 'select-analysis-all-points')
            }
        },
        [cor, points]
    )

    /**
     * Aplica o resultado do popover de seleção em massa.
     *
     * Evolução do antigo "Selecionar todos os pontos": recebe a lista já calculada
     * (malha menos os pontos descontados) e a torna a seleção atual. O desconto tem
     * prioridade, então a seleção anterior é substituída — um ponto marcado à mão que
     * pertença a uma análise descontada acaba desmarcado.
     */
    const selecionarPontosEmMassa = useCallback(
        (pontosParaSelecionar: GeoPointFeature[]) => {
            if (!tipo_analise_selecionada_id) {
                return warningAlert('Selecione uma análise no menu lateral antes de selecionar os pontos.', undefined, undefined, 'select-analysis-all-points')
            }

            const corParaAplicar = cor || '#ffffff'
            const selecionados = new Set(pontosParaSelecionar.map((p) => String(p.NOME)))

            setPoints((prevPoints) =>
                prevPoints.map((p) => ({
                    ...p,
                    COR: selecionados.has(String(p.NOME)) ? corParaAplicar : null,
                }))
            )

            setPontos_selecionados(
                pontosParaSelecionar.map((p) => ({ ...p, COR: corParaAplicar }))
            )

            setSelecionarTodos(pontosParaSelecionar.length === points.length)
        },
        [cor, tipo_analise_selecionada_id, points]
    )

    /**
     * Inverte a seleção: o que estava marcado sai, o que estava desmarcado entra.
     *
     * Reaproveita o caminho da seleção em massa — inverter é só "selecione esta lista",
     * calculada a partir do COR atual de cada ponto (COR preenchida = selecionado).
     */
    const inverterSelecaoPontos = useCallback(() => {
        if (!tipo_analise_selecionada_id) {
            return warningAlert('Selecione uma análise no menu lateral antes de inverter a seleção.', undefined, undefined, 'select-analysis-invert')
        }

        const invertidos = points.filter((p) => !p.COR)

        if (invertidos.length === 0) {
            return warningAlert('Todos os pontos estão selecionados, não há o que inverter.', undefined, undefined, 'invert-empty')
        }

        selecionarPontosEmMassa(invertidos)
    }, [points, tipo_analise_selecionada_id, selecionarPontosEmMassa])

    const limparPontosSelecionados = useCallback(() => {
        setSelecionarTodos(false)
        setPoints((prevPoints) => prevPoints.map((p) => ({ ...p, COR: null })))
        setPontos_selecionados([])
    }, [])

    function deletarPontosSelecionados(cod) {
        // Resolvido fora dos setters: chamar `setPoints` de dentro do updater de
        // outro estado é efeito colateral e pode rodar duas vezes no StrictMode.
        const removida = pontos_selecionados_adicionados.find((item) => item?.id === cod)

        const nomesLiberados = new Set(
            (removida?.pontos_selecionados ?? []).map((ponto: any) => String(ponto?.NOME))
        )

        setFake_loading(true)
        setTimeout(() => {
            setPontos_selecionados_adicionados((prev) => prev.filter((item) => item?.id !== cod))

            // Devolve os pontos daquela análise ao estado "livre" no mapa. Sem isso
            // continuariam pintados, parecendo pertencer a alguma análise — visível
            // no fluxo de micronutrientes, onde a cor é mantida ao adicionar.
            if (nomesLiberados.size > 0) {
                setPoints((pontos) =>
                    pontos.map((ponto) =>
                        nomesLiberados.has(String(ponto?.NOME)) ? { ...ponto, COR: null } : ponto
                    )
                )
            }

            setFake_loading(false)
        }, 500)
    }

    useEffect(() => {
        if (isOpen && initialPoints && Array.isArray(initialPoints)) {
            if (id && pontos_selecionados?.length > 0) {
                const selecionadosSet = new Set(pontos_selecionados.map((p) => String(p.INDICE)))
                const updated = initialPoints.map((ponto) => ({
                    ...ponto,
                    COR: selecionadosSet.has(String(ponto.INDICE)) ? cor : null,
                }))
                setPoints(updated)
            } else {
                setPoints(initialPoints)
            }
        }
    }, [isOpen])

    // ========================
    // RENDERIZAÇÃO
    // ========================
    return (
        <ModalMalhaGeral
            title={'Nova análise'}
            open={isOpen}
            onCancel={onClose}
            width={'90%'}
            centered
            destroyOnClose
            footer={null}
            bodyStyle={{ padding: 0 }}
            keyboard={!fullscreen}
            maskClosable={false}
        >
            <ContainerPontosColeta>

                {/* Sidebar no lado esquerdo (apenas se NÃO fullscreen) */}
                {!fullscreen && (
                    <ControleLateralModal
                        inserirPontos={inserirPontos}
                        tipo_analise_selecionada={tipo_analise_selecionada}
                        setTipo_analise_selecionada={setTipo_analise_selecionada}
                        cor_selecionada={cor}
                        setCor_selecionada={setCor}
                        setTipo_analise_selecionada_id={setTipo_analise_selecionada_id}
                        selecionarTodos={selecionarTodos}
                        grupoAnalises={grupoAnalises}
                        talhao_area={String(talhao_area)}
                        pontos_totais={points.length}
                        pontos={orderBy(pontos_selecionados, 'NOME', 'asc')}
                        pontos_selecionados_adicionados={pontos_selecionados_adicionados}
                        fake_loading={fake_loading}
                        grupoAnalisesTratado={grupoAnalisesTratado}
                        adicionarPontosSelecionados={adicionarPontosSelecionados}
                        selecionarTodosPontos={selecionarTodosPontos}
                        limparPontosSelecionados={limparPontosSelecionados}
                        deletarPontosSelecionados={deletarPontosSelecionados}
                        pontos_da_malha={points}
                        analises_salvas={analises_salvas}
                        selecionarPontosEmMassa={selecionarPontosEmMassa}
                        inverterSelecaoPontos={inverterSelecaoPontos}
                        analises_em_uso={analises_em_uso}
                        em_ciclo_micro={em_ciclo_micro}
                        micro_ja_adicionado={micro_ja_adicionado}
                        aoGerarMicro={gerarMicronutrientes}
                        aoConcluirMicro={concluirMicronutrientes}
                    />
                )}

                <ColunaMapa>

                    {/* Wrapper principal do Mapa */}
                    <WrapperMapa ref={wrapperRef} $fullscreen={fullscreen}>
                        {/*
                          * Segundo ToastContainer, além do que já existe na raiz do projeto.
                          *
                          * A tela cheia usa a API nativa do navegador neste wrapper, e o
                          * navegador renderiza apenas a subárvore do elemento em fullscreen —
                          * o container da raiz fica fora dela, então os toasts de validação
                          * ficariam invisíveis justamente aqui, onde o usuário está mexendo
                          * nos pontos. Este vive dentro do wrapper para acompanhá-lo, e o
                          * zIndex alto o mantém acima do mapa e dos cards flutuantes.
                          *
                          * Os dois containers ficam montados juntos enquanto o fullscreen
                          * está ativo, e nenhum deles tem `containerId` — logo cada toast é
                          * entregue nos dois. É o que garante a continuidade ao sair da tela
                          * cheia: este container é desmontado com os toasts dentro dele, mas
                          * a instância da raiz segue com o timer correndo e volta a aparecer.
                          * O tempo não reinicia, então um toast quase no fim pode sobrar por
                          * poucos instantes.
                          */}
                        {fullscreen && <ToastContainer style={{ zIndex: 999999999 }} />}
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            zoom={14}
                            center={talhaoPath?.[0] || defaultCenter}
                            onLoad={(map) => {
                                mapRef.current = map
                                fitBoundsMap(map)
                            }}
                            onClick={adicionarNovoPontoMalha}
                            options={{
                                mapTypeId: 'satellite',
                                fullscreenControl: false,
                                zoomControl: false,
                                streetViewControl: false,
                                mapTypeControl: false,
                                draggableCursor: inserirPontos ? 'crosshair' : 'grab',
                            }}
                        >
                            {talhaoPath?.length > 0 && (
                                <Polygon onLoad={onPolygonLoad} paths={talhaoPath} options={talhaoOptions} />
                            )}
                            <MarkersMalha
                                pontos={points}
                                tipo_analise_selecionada={tipo_analise_selecionada}
                                pontos_ja_usados={pontos_ja_usados}
                                moverPontos={moverPontos}
                                moverPontosMalha={moverPontosMalha}
                                selecionarPontosMalha={selecionarPontosMalha}
                            />

                            {/* === ELEMENTOS INTERNOS === */}

                            {/* 0. Card com Informações (Topo) - Apenas quando não está em fullscreen */}
                            <Render condition={!fullscreen}>
                                <InfosMalha
                                    talhao_area={talhao_area}
                                    total_pontos={points.length}
                                    pontos_nunca_usados={pontos_nunca_usados}
                                >
                                    {/* Botão de tela cheia dentro do mapa */}
                                    <AcoesInfo>
                                        <ButtonCustom
                                            onClick={toggleFullscreen}
                                            type='tertiary'
                                            icon={<FontAwesomeIcon icon={faExpand} />}
                                        >
                                            Tela cheia
                                        </ButtonCustom>
                                    </AcoesInfo>
                                </InfosMalha>
                            </Render>

                            {/* 1. Card Alterações: Reposicionado abaixo do info card */}
                            <Render condition={!fullscreen}>
                                <AlteracoesMalha
                                    inserirPontos={inserirPontos}
                                    setInserirPontos={setInserirPontos}
                                    moverPontos={moverPontos}
                                    setMoverPontos={setMoverPontos}
                                />
                            </Render>

                            {/* 2. Overlay de aviso */}
                            <OverlayAnalise
                                tipo_analise_selecionada={tipo_analise_selecionada}
                                fullscreen={fullscreen}
                            />

                            {/* 3. Sidebar Fullscreen (Consolidado) */}
                            <Render condition={fullscreen}>
                                <CardFullscreen
                                    salvarPontosSelecionados={salvarPontosSelecionados}
                                    adicionarPontosSelecionados={adicionarPontosSelecionados}
                                    selecionarTodosPontos={selecionarTodosPontos}
                                    limparPontosSelecionados={limparPontosSelecionados}
                                    deletarPontosSelecionados={deletarPontosSelecionados}
                                    analises_salvas={analises_salvas}
                                    selecionarPontosEmMassa={selecionarPontosEmMassa}
                                    inverterSelecaoPontos={inverterSelecaoPontos}
                                    analises_em_uso={analises_em_uso}
                        em_ciclo_micro={em_ciclo_micro}
                        aoGerarMicro={gerarMicronutrientes}
                        aoConcluirMicro={concluirMicronutrientes}
                                    grupoAnalisesTratado={grupoAnalisesTratado}
                                    pontos_selecionados_adicionados={pontos_selecionados_adicionados}
                                    fake_loading={fake_loading}
                                    toggleFullscreen={toggleFullscreen}
                                    inserirPontos={inserirPontos}
                                    moverPontos={moverPontos}
                                    talhao_area={talhao_area}
                                    pontos={points}
                                    pontos_nunca_usados={pontos_nunca_usados}
                                    setInserirPontos={setInserirPontos}
                                    setMoverPontos={setMoverPontos}
                                    tipo_analise_selecionada={tipo_analise_selecionada}
                                    setTipo_analise_selecionada={setTipo_analise_selecionada}
                                    setTipo_analise_selecionada_id={setTipo_analise_selecionada_id}
                                    pontos_selecionados={pontos_selecionados}
                                    selecionarTodos={selecionarTodos}
                                    cor={cor}
                                    setCor={setCor}
                                    grupoAnalises={grupoAnalises}
                                />
                            </Render>
                        </GoogleMap>
                    </WrapperMapa>
                </ColunaMapa>
            </ContainerPontosColeta>

            <Render condition={!fullscreen}>
                <FooterModal>
                    <AcaoSalvar
                        salvarPontosSelecionados={salvarPontosSelecionados}
                        total_pontos_selecionados={pontos_selecionados.length}
                        total_analises_adicionadas={pontos_selecionados_adicionados.length}
                    />
                </FooterModal>
            </Render>
        </ModalMalhaGeral>
    )
}
